import { z } from "zod";
//#region src/departed-app-grant-reap.schemas.ts
/**
* The contract between the zion-operator program and core-api for the departed-app
* grant reap (plan `2026-10-08-app-departure-grant-revoke`, decisions D1 to D5,
* rulings R-002 and R-003).
*
* After a full sync, the operator program sends core-api a list of app ids. Each
* app has an `apps_catalog` row and is absent from the envelope's `apps`, `kept`
* and `withheld`. core-api checks each id against its own tables. It revokes the
* role app-permission grants of each app that passes, and refuses each other app
* with one reason. The route sits behind `OperatorTokenGuard`.
*
* The checks guard against an operator program bug: an app with live rows or a
* recent change is refused. They cannot see `withheld` or `kept`, so the operator
* program alone must leave those apps out (R-003).
*/
/** `POST`, operator token. The core-api route of the reap. */
const DEPARTED_APP_GRANT_REAP_ROUTE = "/internal/v1/auth/departed-app-grants/reap";
/** The most app ids one request may carry. */
const DEPARTED_APP_GRANT_REAP_MAX_APPS = 200;
/**
* The reasons core-api refuses one app, one per failed check, in the order core-api
* runs the checks:
*
* 1. `no_catalog_row`: the app has no `release.apps_catalog` row.
* 2. `rows_present`: an `app_manifests`, `fixture_versions` or
*    `app_permission_declarations` row still exists for the app.
* 3. `history_not_uninstall`: the newest `release.app_version_history` row of the
*    app exists and is not an `uninstall` row.
* 4. `within_grace`: the app's last change is less than 24 h old. The last change is
*    the later of that newest history row and the `apps_catalog` row's `synced_at`.
*
* An app with no history row passes check 3, and its catalog row alone decides
* check 4. Two classes have no history row: an app whose rows the 90-day retention
* job deleted (R-002), and an app pinned only to a private channel (R-008).
*/
const DEPARTED_APP_GRANT_REAP_REFUSAL_REASONS = [
	"no_catalog_row",
	"rows_present",
	"history_not_uninstall",
	"within_grace"
];
/** The request body: 1 to 200 app ids, each a uuid, no id twice. */
const DepartedAppGrantReapRequestSchema = z.object({ appIds: z.array(z.uuid()).min(1).max(200).refine((ids) => new Set(ids).size === ids.length, { message: "appIds must not repeat an id" }) }).strict();
const ReapedResultSchema = z.object({
	appId: z.uuid(),
	outcome: z.literal("reaped"),
	/** The number of grants core-api revoked for the app over every org. Zero is valid. */
	revoked: z.number().int().nonnegative()
}).strict();
const RefusedResultSchema = z.object({
	appId: z.uuid(),
	outcome: z.literal("refused"),
	reason: z.enum(DEPARTED_APP_GRANT_REAP_REFUSAL_REASONS)
}).strict();
const FailedResultSchema = z.object({
	appId: z.uuid(),
	/**
	* core-api threw while it checked or reaped this app. The cause is in the core-api log.
	* Revokes of other orgs of the app that committed before the error stay. The caller
	* sends the app again at its next sync (R-007).
	*/
	outcome: z.literal("failed")
}).strict();
/**
* The result for one app: `reaped` with a count, `refused` with a reason, or `failed` when
* core-api threw for that app. One failed app never hides the results of the others (R-007).
*/
const DepartedAppGrantReapResultSchema = z.discriminatedUnion("outcome", [
	ReapedResultSchema,
	RefusedResultSchema,
	FailedResultSchema
]);
/**
* The response body, always with status 200 for a valid request. core-api writes one
* result per requested app id, in the order of the request, and no app id twice, failed
* apps included (R-007). The schema cannot see the request, so the
* caller must check that the result ids equal the requested ids.
*/
const DepartedAppGrantReapResponseSchema = z.object({ results: z.array(DepartedAppGrantReapResultSchema).min(1).max(200).refine((rows) => new Set(rows.map((r) => r.appId)).size === rows.length, { message: "results must not repeat an appId" }) }).strict();
//#endregion
export { DEPARTED_APP_GRANT_REAP_MAX_APPS, DEPARTED_APP_GRANT_REAP_REFUSAL_REASONS, DEPARTED_APP_GRANT_REAP_ROUTE, DepartedAppGrantReapRequestSchema, DepartedAppGrantReapResponseSchema, DepartedAppGrantReapResultSchema };

//# sourceMappingURL=departed-app-grant-reap.schemas.js.map